import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Clock, MessagesSquare, Quote, Trophy, KeyRound } from "lucide-react";
import { toast } from "sonner";

export function ClockButton() {
  const [rec, setRec] = useState(null);
  useEffect(() => { api.get("/attendance/today").then(({ data }) => setRec(data)).catch(() => {}); }, []);
  const on = rec?.sessions?.length && !rec.sessions[rec.sessions.length - 1].out;
  const toggle = async () => {
    try { const { data } = await api.post(on ? "/attendance/clock-out" : "/attendance/clock-in"); setRec(data); toast.success(on ? "Clocked out" : "Clocked in"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <button onClick={toggle} data-testid="clock-btn" className={`h-9 px-3 rounded-full border text-xs font-semibold flex items-center gap-1.5 transition-colors ${on ? "bg-emerald-50 border-emerald-300 text-emerald-700 hover:bg-emerald-100" : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
      <span className={`w-2 h-2 rounded-full ${on ? "bg-emerald-500 animate-pulse" : "bg-slate-300"}`} /><Clock className="w-3.5 h-3.5" /><span className="hidden md:inline">{on ? "Clock Out" : "Clock In"}</span>
    </button>
  );
}

export function ChatButton() {
  const navigate = useNavigate();
  const [n, setN] = useState(0);
  useEffect(() => {
    const f = () => api.get("/chat/unread").then(({ data }) => setN(data.count)).catch(() => {});
    f(); const t = setInterval(f, 15000); return () => clearInterval(t);
  }, []);
  return (
    <button onClick={() => navigate("/chat")} className="relative w-10 h-10 rounded-lg hover:bg-slate-100 flex items-center justify-center" data-testid="chat-btn">
      <MessagesSquare className="w-5 h-5 text-slate-600" />
      {n > 0 && <span className="absolute top-1.5 right-1.5 w-4 h-4 rounded-full bg-emerald-500 text-white text-[10px] flex items-center justify-center font-bold" data-testid="chat-unread-badge">{n}</span>}
    </button>
  );
}

export function MorningBriefing() {
  const { user } = useAuth();
  const [b, setB] = useState(null);
  const key = `ohc_brief_${user.id}_${new Date().toISOString().slice(0, 10)}`;
  useEffect(() => { if (!localStorage.getItem(key)) api.get("/my/briefing").then(({ data }) => setB(data)).catch(() => {}); }, [key]);
  const close = () => { localStorage.setItem(key, "1"); setB(null); };
  const hr = new Date().getHours();
  const greet = hr < 12 ? "Good morning" : hr < 17 ? "Good afternoon" : "Good evening";
  return (
    <Dialog open={!!b} onOpenChange={(o) => !o && close()}>
      <DialogContent className="bg-white max-w-lg p-0 overflow-hidden" data-testid="briefing-modal">
        {b && <>
          <div className="bg-gradient-to-br from-[#312E81] to-[#4F46E5] text-white p-6">
            <div className="text-[11px] uppercase tracking-widest text-indigo-200">Daily Briefing · {new Date(b.date).toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long" })}</div>
            <div className="font-heading text-2xl font-extrabold mt-1">{greet}, {b.name}</div>
          </div>
          <div className="p-6 space-y-4">
            <div className="border-l-4 border-[#4F46E5] bg-indigo-50 p-3 rounded-r-lg text-sm italic text-slate-700"><Quote className="w-4 h-4 inline mr-1 text-[#4F46E5]" />{b.quote}<div className="text-xs not-italic text-slate-500 mt-1">— {b.quote_by}</div></div>
            <div className="grid grid-cols-3 gap-2">
              {[["Last week", b.last_week], ["This week", b.this_week], ["Month so far", `${b.month_so_far}${b.target ? ` / ${b.target}` : ""}`]].map(([l, v]) => <div key={l} className="border rounded-lg p-2 text-center"><div className="text-[10px] uppercase text-slate-400">{l}</div><div className="text-2xl font-heading font-extrabold text-[#312E81]">{v}</div><div className="text-[10px] text-slate-400">admissions</div></div>)}
            </div>
            {b.top?.length > 0 && <div><div className="text-xs font-semibold text-slate-500 uppercase mb-1">Top performers this month</div>{b.top.map((t, i) => <div key={i} className="flex items-center gap-2 text-sm py-1"><Trophy className={`w-4 h-4 ${["text-amber-500", "text-slate-400", "text-orange-600"][i]}`} /><span className="flex-1">{t.name}</span><b>{t.admissions}</b></div>)}</div>}
            <div className="text-xs text-slate-500">{b.followups_due} follow-ups due today or overdue.</div>
            <Button onClick={close} className="w-full bg-[#4F46E5] hover:bg-[#4338CA]" data-testid="briefing-close">Let's make today count</Button>
          </div>
        </>}
      </DialogContent>
    </Dialog>
  );
}

export function ChangePasswordDialog({ open, onOpenChange, forced }) {
  const { user, setUser } = useAuth();
  const [f, setF] = useState({ current_password: "", new_password: "", confirm: "" });
  const save = async () => {
    if (f.new_password !== f.confirm) { toast.error("New passwords don't match"); return; }
    try {
      await api.post("/auth/change-password", { current_password: f.current_password, new_password: f.new_password });
      toast.success("Password changed"); setUser({ ...user, must_change_password: false }); setF({ current_password: "", new_password: "", confirm: "" }); onOpenChange?.(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={forced ? () => {} : onOpenChange}>
      <DialogContent className={`bg-white ${forced ? "[&>button]:hidden" : ""}`} data-testid="change-password-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><KeyRound className="w-5 h-5 text-[#4F46E5]" /> {forced ? "Set your own password" : "Change password"}</DialogTitle></DialogHeader>
        {forced && <p className="text-sm text-slate-500 -mt-2">You signed in with a temporary password. Please choose a new one to continue.</p>}
        <div className="space-y-3">
          {[["current_password", forced ? "Temporary password" : "Current password"], ["new_password", "New password (min 8 characters)"], ["confirm", "Confirm new password"]].map(([k, l]) => (
            <div key={k}><Label>{l}</Label><Input type="password" value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`cp-${k}`} /></div>
          ))}
        </div>
        <DialogFooter><Button onClick={save} className="bg-[#4F46E5] hover:bg-[#4338CA]" data-testid="cp-save">Save password</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
